/**
 * Instruction document — the shape a skill's free-text instructions are stored in
 * (PRD Feature 3, VOX-184 for the editor / VOX-186 for the wire contract).
 *
 * These types are a **verbatim mirror** of the backend contract
 * (`shared/processInstructions/types.ts`, story VOX-186): same field names, same
 * `schema_version`, same segment discriminants. They live here, and not behind
 * `@shared/processInstructions`, because that module is not reachable from the
 * frontend bundle yet — `app/vite.config.ts` aliases `@shared/*` only to
 * subpaths of the RTA submodule.
 * When the alias lands (with the persistence wiring of VOX-196) this file becomes
 * a re-export instead of a copy, and nothing that imports it has to change.
 *
 * The invariant that makes that swap safe: **HTML is never the format**. The
 * editor's contentEditable DOM is a rendering of a document, never the document
 * itself, and chips reference resources by id only — secrets never appear here.
 */

/** Current persisted schema version. Bump when the wire format changes. */
export const CURRENT_SCHEMA_VERSION = 1 as const;

/** Every segment type the document can hold. */
export type InstructionSegmentType =
  | 'text'
  | 'integration_chip'
  | 'mcp_chip'
  | 'table_op_chip'
  | 'agent_chip';

/** Plain text / markdown. Newlines and markdown are preserved verbatim, never HTML. */
export interface TextSegment {
  type: 'text';
  content: string;
}

/**
 * Chip referencing a custom integration by id.
 *
 * `display_label` is a presentation hint, not identity: it is what the chip reads
 * when the resource cannot be resolved. Identity is always `integration_id`.
 */
export interface IntegrationChipSegment {
  type: 'integration_chip';
  integration_id: string;
  display_label?: string;
}

/** Chip referencing an MCP server by id. Credentials never travel on the chip. */
export interface McpChipSegment {
  type: 'mcp_chip';
  mcp_server_id: string;
  display_label?: string;
}

/**
 * Actions a table operation can declare.
 *
 * Note for anyone porting from the design prototype: it calls the second one
 * `insert`. The contract says `create`, and `instructions/canonical.ts`
 * normalises it, so `action` and `canonical` can never disagree.
 */
export type TableOpAction = 'read' | 'create' | 'update' | 'delete';

/**
 * Chip declaring a dynamic-table operation.
 *
 * `columns: '*'` means the whole row; otherwise an ordered list of column names.
 * `canonical` is stored, not derived on read, so the assistant and the execution
 * engine consume the same string the editor showed.
 */
export interface TableOpChipSegment {
  type: 'table_op_chip';
  table_id: string;
  action: TableOpAction;
  columns: string[] | '*';
  /** `{action} {tableName} [{col}, …]` or `{action} {tableName} [*]`. */
  canonical: string;
  display_label?: string;
}

/**
 * Chip referencing another agent of the same account, invoked as a delegation
 * step (Feature 13 / SL-1730). Identity is `agent_id`; the invocation mechanism
 * (internal delegation vs A2A) is the engine's concern, never stored on the chip.
 * `display_label` is a presentation hint, not identity — same as the other chips.
 */
export interface AgentChipSegment {
  type: 'agent_chip';
  agent_id: string;
  display_label?: string;
}

export type InstructionSegment =
  | TextSegment
  | IntegrationChipSegment
  | McpChipSegment
  | TableOpChipSegment
  | AgentChipSegment;

/** Any segment that is a chip — i.e. everything but text. */
export type ChipSegment = Exclude<InstructionSegment, TextSegment>;

/** The versioned document. This, and only this, is what gets persisted. */
export interface InstructionDocument {
  schema_version: number;
  segments: InstructionSegment[];
}

/**
 * State of the resource a chip points at, computed on read — never persisted.
 *
 * `disconnected` is for a table that still exists but is no longer linked to the
 * agent; `missing` is for one that was deleted outright.
 */
export type ChipResolutionStatus = 'active' | 'inactive' | 'missing' | 'disconnected';

/**
 * Catalogs supplied by whoever knows about resources, so chips can be resolved.
 *
 * Semantics, following the contract: a map that is *present* but lacks the id
 * resolves to `missing`. A map that is *absent* resolves to `active` — no
 * catalog is not evidence of a deleted resource, and until the resource panels
 * land (VOX-190 / VOX-191 / VOX-199) there is no catalog to consult.
 */
export interface ChipResolutionContext {
  integrations?: Record<string, ChipResolutionStatus>;
  mcp_servers?: Record<string, ChipResolutionStatus>;
  tables?: Record<string, ChipResolutionStatus>;
  /**
   * Delegable agent status (Feature 13). `missing` when the agent no longer
   * exists; `inactive` when it exists but cannot be called.
   */
  agents?: Record<string, ChipResolutionStatus>;
}

/** An empty document, as a brand-new skill starts. */
export const EMPTY_INSTRUCTION_DOCUMENT: InstructionDocument = {
  schema_version: CURRENT_SCHEMA_VERSION,
  segments: [],
};
